import useTranslation from 'shared/hooks/useTranslation';
import styled from 'styled-components';
import SocialIconLink from 'shared/components/SocialIconLinkV2';
import { Wrapper } from './styled';

function Management(): JSX.Element {
  const { t } = useTranslation('ourTeamPage');

  type Management = {
    videoSrc: string;
    name: string;
    position: string;
    description: string;
    linkedIn: string;
  };

  const MANAGEMENT: Management[] = [
    {
      videoSrc: 'https://cdn.trustmate.io/video/our-team/jerzy.webm',
      name: 'Jerzy Krawczyk',
      position: 'Chief Executive Officer',
      description: `${t('jerzyKrawczyk')} ⛷️`,
      linkedIn: 'https://www.linkedin.com/in/jerzy-krawczyk-215236a6/',
    },
    {
      videoSrc: 'https://cdn.trustmate.io/video/our-team/robert.webm',
      name: 'Robert Gołuński',
      position: 'Head of Growth',
      description: `${t('robertGolunski')} 🍷`,
      linkedIn: 'https://www.linkedin.com/in/robert-go%C5%82u%C5%84ski-4aa21033/',
    },
    {
      videoSrc: 'https://cdn.trustmate.io/video/our-team/michal.webm',
      name: 'Michał Zagdan',
      position: 'Chief Technology Officer',
      description: `${t('michalZagdan')} 💻`,
      linkedIn: 'https://www.linkedin.com/in/micha%C5%82-zagdan-5181064/',
    },
    {
      videoSrc: 'https://cdn.trustmate.io/video/our-team/pawel.webm',
      name: 'Paweł Kubiak',
      position: 'Chief Product Officer',
      description: `${t('pawelKubiak')} 🐕‍🦺🐕`,
      linkedIn: 'https://www.linkedin.com/in/pawe%C5%82-kubiak-5555b5113/',
    },
  ];

  return (
    <StyledWrapper maxWidth>
      <header>
        <HeaderH2>{t('getToKnowUs')}</HeaderH2>
        <HeaderH3>Management</HeaderH3>
      </header>
      <Cards>
        {MANAGEMENT.map((person, index) => (
          <Card key={person.name}>
            <VideoWrapper>
              <video
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onMouseOut={(event) => event.target.play()}
                autoPlay
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onMouseOver={(event) => event.target.pause()}
                onFocus={(event) => event.target.pause()}
                onBlur={(event) => event.target.play()}
                loop
                muted
              >
                <source src={person.videoSrc} type="video/mp4" />
              </video>
            </VideoWrapper>
            <NameAndPosition>
              <div>
                <Name>{person.name}</Name>
                <Position>{person.position}</Position>
              </div>
              <ButtonWrapper style={{ animationDelay: `${Math.floor(Math.random() * index * 1000) + 500}ms` }}>
                <SocialIconLink
                  href={person.linkedIn}
                  src="/img/social-icons/linked.svg"
                  alt="LinkedIn logo"
                  width={15}
                  height={15}
                  isTarget
                />
              </ButtonWrapper>
            </NameAndPosition>
            <Description>{person.description}</Description>
          </Card>
        ))}
      </Cards>
    </StyledWrapper>
  );
}

const HeaderH2 = styled.h2`
  color: #fff;
  text-align: center;
  font-size: 3.2rem;
  font-weight: 500;
  line-height: 83%;
  letter-spacing: 0.754px;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 7.2rem;
    line-height: 116%;
  }
`;

const HeaderH3 = styled.h3`
  text-align: center;
  font-size: 2.4rem;
  font-weight: 600;
  line-height: 349%;
  letter-spacing: 0.754px;
  background: linear-gradient(93deg, #ffa727 26.7%, #fc8103 98.5%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 5.4rem;
    line-height: 155%;
  }
`;

const Cards = styled.div`
  align-items: stretch;
  display: grid;
  gap: 3rem;
  justify-content: start;
  grid-template-columns: 1fr 1fr;
  overflow-y: auto;
  max-width: 100%;
  margin-top: 1.6rem;
  height: 100%;

  @media ${({ theme }) => theme.DEVICE.LG} {
    align-items: stretch;
    display: flex;
    gap: 3rem;
    justify-content: start;
  }
`;

const Card = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 16px;
  background-color: #0e0e01;
  min-width: 15.5rem;

  @media ${({ theme }) => theme.DEVICE.MD} {
    width: 30.4rem;
  }
`;

const ButtonWrapper = styled.div`
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    80% {
      transform: scale(1);
    }
    90% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
  animation: pulse 6s infinite;
`;

const VideoWrapper = styled.div`
  video {
    width: 100%;
    height: auto;
  }
`;

const NameAndPosition = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 0.6rem;
  padding: 0 1.6rem;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
`;

const Name = styled.span`
  color: #fff;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 138%;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 2rem;
    line-height: 150%;
  }
`;

const Position = styled.span`
  color: #fc8000;
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 120%;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 1.6rem;
    line-height: 134%;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 132%;
  letter-spacing: -0.2px;
  padding: 1.6rem;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 1.5rem;
    line-height: 143%;
  }
`;

const StyledWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default Management;
